// src/components/CancellationPolicy.js
import React from "react";
import { Container, Box, Typography, Paper, Button } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { MERCHANT_LEGAL } from "../config/merchantLegalConfig";
import PolicyContactSection from "./PolicyContactSection";

const CancellationPolicy = ({ onBack, primaryBlue, glassStyles }) => {
  const defaultPrimaryBlue = primaryBlue || {
    50: "#F0F8FF",
    100: "#E6F0FA",
    500: "#1E40AF",
    600: "#002366",
    700: "#001A4D",
    800: "#0B1C48",
  };

  const defaultGlassStyles = glassStyles || {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${defaultPrimaryBlue[50]} 0%, ${defaultPrimaryBlue[100]} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{
            mb: 3,
            color: defaultPrimaryBlue[700],
            fontWeight: 600,
          }}
        >
          Back
        </Button>

        <Paper
          elevation={0}
          sx={{
            ...defaultGlassStyles,
            p: { xs: 3, md: 5 },
            bgcolor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: defaultPrimaryBlue[700],
              mb: 2,
            }}
          >
            Cancellation Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: January 2025
          </Typography>

          <Box sx={{ "& > *": { mb: 3 } }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                1. Overview
              </Typography>
              <Typography variant="body1" paragraph>
                At InfinityWhale, we understand that circumstances may change after placing an order. This Cancellation Policy outlines the terms and conditions for canceling orders, including timelines, refund eligibility, and procedures.
              </Typography>
              <Typography variant="body1" paragraph>
                Please read this policy carefully before placing an order. By placing an order, you agree to the terms outlined in this Cancellation Policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                2. Order Cancellation by Customer
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                2.1 Before Payment Confirmation
              </Typography>
              <Typography variant="body1" paragraph>
                If your payment has not been confirmed by our admin team:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • You can cancel your order at any time<br />
                • Contact us immediately at {MERCHANT_LEGAL.supportEmail} or {MERCHANT_LEGAL.supportPhone}<br />
                • Provide your order number and reason for cancellation<br />
                • Full refund will be processed within 5-7 business days<br />
                • No cancellation charges apply
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                2.2 After Payment Confirmation (Before Shipping)
              </Typography>
              <Typography variant="body1" paragraph>
                Once payment is confirmed but before the order is shipped:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • You can cancel within 24 hours of payment confirmation<br />
                • Contact customer support immediately<br />
                • Full refund minus payment gateway charges (if applicable)<br />
                • Refund processed within 5-7 business days<br />
                • Referral points (if any) will be reversed
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                2.3 After Order is Shipped
              </Typography>
              <Typography variant="body1" paragraph>
                Once the order has been shipped:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Order cannot be cancelled<br />
                • You may refuse delivery and initiate a return<br />
                • Return shipping charges may apply<br />
                • Refer to our Return & Refund Policy for details<br />
                • Refund will be processed after we receive the returned product
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                3. How to Cancel an Order
              </Typography>
              <Typography variant="body1" paragraph>
                To cancel your order, follow these steps:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                <strong>Step 1:</strong> Contact us immediately<br />
                • Email: {MERCHANT_LEGAL.supportEmail}<br />
                • Phone: {MERCHANT_LEGAL.supportPhone}<br />
                • Business Hours: Mon-Sat, 9 AM - 6 PM IST<br />
                <br />
                <strong>Step 2:</strong> Provide the following information:<br />
                • Order number (found in your confirmation email)<br />
                • Registered email address or phone number<br />
                • Reason for cancellation<br />
                • Preferred refund method<br />
                <br />
                <strong>Step 3:</strong> Wait for confirmation<br />
                • Our team will review your request within 2-4 hours<br />
                • You will receive a cancellation confirmation email<br />
                • Refund will be initiated as per the timeline<br />
                <br />
                <strong>Step 4:</strong> Track your refund<br />
                • Check your email for refund status updates<br />
                • Refund will be credited to your original payment method
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                4. Non-Cancellable Items
              </Typography>
              <Typography variant="body1" paragraph>
                The following items cannot be cancelled once the order is confirmed:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Subscriptions:</strong> Activation fees and subscriptions are non-refundable once activated<br />
                • <strong>Digital Products:</strong> Downloadable content, gift cards, and vouchers<br />
                • <strong>Custom/Personalized Items:</strong> Products made to order or customized for you<br />
                • <strong>Perishable Goods:</strong> Food items, flowers, or time-sensitive products<br />
                • <strong>Sale/Clearance Items:</strong> Products marked as final sale<br />
                • <strong>Bulk Orders:</strong> Orders with 10+ units (special terms apply)
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                5. Refund Process for Cancelled Orders
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                5.1 Refund Timeline
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Before Payment Confirmation:</strong> 5-7 business days<br />
                • <strong>After Payment Confirmation:</strong> 7-10 business days<br />
                • <strong>After Shipping (Return Required):</strong> 10-14 business days
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                5.2 Refund Method
              </Typography>
              <Typography variant="body1" paragraph>
                Refunds will be processed to the original payment method:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>UPI:</strong> 3-5 business days<br />
                • <strong>Bank Transfer:</strong> 5-7 business days<br />
                • <strong>Credit/Debit Card:</strong> 7-10 business days<br />
                • <strong>Digital Wallet:</strong> 2-3 business days
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                5.3 Refund Amount
              </Typography>
              <Typography variant="body1" paragraph>
                The refund amount will include:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Full product price (including GST)<br />
                • Shipping charges (if order not shipped)<br />
                • Any applicable discounts or promotions
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>Deductions (if applicable):</strong>
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Payment gateway charges (2-3% for certain payment methods)<br />
                • Return shipping costs (if order was shipped)<br />
                • Restocking fee (up to 10% for certain products)
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                6. Order Cancellation by InfinityWhale
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to cancel orders in the following situations:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>Product Unavailability:</strong> If the product is out of stock or discontinued<br />
                • <strong>Pricing Errors:</strong> If there was a technical error in pricing<br />
                • <strong>Payment Issues:</strong> If payment verification fails or is fraudulent<br />
                • <strong>Delivery Restrictions:</strong> If we cannot deliver to your location<br />
                • <strong>Suspicious Activity:</strong> If we detect fraudulent or suspicious behavior<br />
                • <strong>Force Majeure:</strong> Due to natural disasters, strikes, or other uncontrollable events
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>In case of cancellation by us:</strong>
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • You will be notified immediately via email and SMS<br />
                • Full refund will be processed within 5-7 business days<br />
                • No cancellation charges will apply<br />
                • We may offer alternative products or store credit
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                7. Partial Order Cancellation
              </Typography>
              <Typography variant="body1" paragraph>
                If you want to cancel only some items from a multi-item order:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Contact us before the order is shipped<br />
                • Specify which items you want to cancel<br />
                • Partial refund will be processed for cancelled items<br />
                • Remaining items will be shipped as scheduled<br />
                • Shipping charges may be recalculated based on remaining order value
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                8. Impact on Referral Program
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>8.1 Referral Points Reversal:</strong> If an order placed through a referral is cancelled:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • All referral points credited for that order will be reversed<br />
                • This applies to all levels of the referral chain<br />
                • Points will be deducted from referrer accounts<br />
                • If points have been withdrawn, the amount may be adjusted in future earnings
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>8.2 Subscriptions:</strong> Cancellation of subscriptions is not allowed once activated. Activation fees are non-refundable.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                9. Modification of Orders
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>9.1 Before Shipping:</strong> You may request modifications to your order (address, quantity, product) before it is shipped. Contact us immediately with your request.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>9.2 After Shipping:</strong> Orders cannot be modified once shipped. You may refuse delivery and place a new order, or return the product as per our Return Policy.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>9.3 Address Changes:</strong> Address changes are only possible before the order is shipped. We are not responsible for delivery failures due to incorrect addresses provided by customers.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                10. Cancellation Charges
              </Typography>
              <Typography variant="body1" paragraph>
                Cancellation charges may apply in the following cases:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • <strong>After Shipping:</strong> Return shipping charges (₹50-₹150 depending on location)<br />
                • <strong>Payment Gateway Fees:</strong> 2-3% of order value (for certain payment methods)<br />
                • <strong>Restocking Fee:</strong> Up to 10% for opened or used products<br />
                • <strong>Custom Products:</strong> 100% non-refundable (no cancellation allowed)
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                11. Failed Delivery and Cancellation
              </Typography>
              <Typography variant="body1" paragraph>
                If delivery fails due to the following reasons, the order will be automatically cancelled:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Customer not available after 3 delivery attempts<br />
                • Incorrect or incomplete address provided<br />
                • Customer refuses to accept delivery<br />
                • Customer not reachable via phone or email
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>In such cases:</strong>
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Order will be returned to our warehouse<br />
                • Refund will be processed minus shipping charges (both ways)<br />
                • Restocking fee may apply<br />
                • Refund timeline: 10-14 business days after product return
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                12. Bulk Orders and Corporate Orders
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>12.1 Bulk Orders:</strong> Orders with 10+ units have special cancellation terms:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Cancellation allowed only within 12 hours of order placement<br />
                • Cancellation charges: 15-20% of order value<br />
                • No cancellation allowed after production/packaging begins
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                <strong>12.2 Corporate Orders:</strong> Corporate or business orders follow customized cancellation policies as per the agreement.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                13. Exceptions and Special Cases
              </Typography>
              <Typography variant="body1" paragraph>
                We may make exceptions to this policy in the following cases:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Medical emergencies or family emergencies<br />
                • Natural disasters or force majeure events<br />
                • Errors on our part (wrong product, defective product)<br />
                • Significant delays in delivery (beyond 15 days)
              </Typography>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                Please contact us with supporting documentation, and we will review your case on a priority basis.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                14. Consumer Rights
              </Typography>
              <Typography variant="body1" paragraph>
                This policy does not affect your statutory rights under the Consumer Protection Act, 2019. You have the right to:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                • Cancel orders for defective or misrepresented products<br />
                • Seek compensation for unfair trade practices<br />
                • File complaints with consumer forums<br />
                • Receive timely refunds as per law
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                15. Policy Updates
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify this Cancellation Policy at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after changes constitutes acceptance of the updated policy.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: defaultPrimaryBlue[700] }}>
                16. Contact Us
              </Typography>
              <PolicyContactSection intro="For order cancellations or questions about this policy, please contact us:" />
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CancellationPolicy;
